import { useState, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import axios from "axios";
import { getAccessToken } from "config/token";
import { NEXUS_API_BASE_URL, NEXUS_API_BASE_URL_V2 } from "const";
import type { GalleryProfile } from "components/AppGallery/types";

interface MarketplaceApiResponse {
  configs: GalleryProfile[];
  total: number;
}

interface UseMarketplaceAppsResult {
  profiles: GalleryProfile[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useMarketplaceApps = (profileType?: string): UseMarketplaceAppsResult => {
  const msalInstance = useMsal();
  const [profiles, setProfiles] = useState<GalleryProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchApps = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken(msalInstance);
      const bearerToken = `Bearer ${token?.token ?? ""}`;

      const params = profileType ? { profile_type: profileType } : {};
      const url = `${NEXUS_API_BASE_URL}/nexus/marketplace-config`;

      const res = await axios.get<MarketplaceApiResponse>(url, {
        headers: { Authorization: bearerToken },
        params,
      });

      setProfiles(res.data.configs ?? []);
    } catch (err) {
      setError("Failed to load marketplace applications.");
      setProfiles([]);
    } finally {
      setIsLoading(false);
    }
  }, [msalInstance, profileType]);

  useEffect(() => {
    fetchApps();
  }, [fetchApps]);

  return { profiles, isLoading, error, refetch: fetchApps };
};
